<template>
  <div class="service-order-intake">
    <KTCodePreview v-bind:title="'Tiếp nhận xe'">
      <template v-slot:preview>
        <!-- Card 1: Thông tin xe -->
        <div class="intake-card">
          <h6 class="intake-card-title">Thông tin xe (bắt buộc)</h6>

          <label class="intake-label">Tìm xe / khách hàng</label>
          <div class="position-relative">
            <b-form-input
              v-model="keyword"
              size="sm"
              autocomplete="off"
              placeholder="Tìm theo biển số, số khung, tên, SĐT hoặc MST"
              @input="onSearchInput"
            />
            <div v-if="searching" class="intake-search-hint">Đang tìm…</div>
            <b-list-group v-if="showResults" class="intake-results">
              <b-list-group-item
                v-for="v in searchResults"
                :key="v.id"
                button
                @click="selectVehicle(v)"
              >
                <b>{{ v.licensePlate }}</b>
                · {{ v.productName }} · {{ v.customerName }} ·
                {{ v.customerPhone }}
              </b-list-group-item>
              <b-list-group-item v-if="!searchResults.length" disabled>
                Không tìm thấy thông tin xe
              </b-list-group-item>
            </b-list-group>
          </div>

          <div class="mt-3">
            <b-button
              size="sm"
              variant="outline-primary"
              @click="openAddCustomer"
            >
              <i class="fas fa-plus"></i> Thêm KH mới
            </b-button>
          </div>

          <!-- Vehicle Profile Panel -->
          <div v-if="selectedVehicle" class="intake-profile mt-4">
            <div class="d-flex align-items-center justify-content-between">
              <h6 class="intake-card-title mb-0">Hồ sơ xe</h6>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="editVehicle"
              >
                <i class="fas fa-edit"></i> Sửa
              </b-button>
            </div>
            <b-row>
              <b-col md="6" class="intake-field">
                <span>Biển số xe:</span>
                <b>{{ selectedVehicle.licensePlate }}</b>
              </b-col>
              <b-col md="6" class="intake-field">
                <span>Tên sản phẩm:</span> {{ selectedVehicle.productName }}
              </b-col>
              <b-col md="6" class="intake-field">
                <span>Số khung:</span>
                {{ selectedVehicle.chassisNumber || '—' }}
              </b-col>
              <b-col md="6" class="intake-field">
                <span>Khách hàng:</span> {{ selectedVehicle.customerName }}
              </b-col>
              <b-col md="6" class="intake-field">
                <span>Số điện thoại:</span> {{ selectedVehicle.customerPhone }}
              </b-col>
              <b-col md="6" class="intake-field">
                <span>Xe showroom bán ra:</span>
                <b-badge
                  :variant="
                    selectedVehicle.isSoldByShowroom ? 'success' : 'secondary'
                  "
                >
                  {{ selectedVehicle.isSoldByShowroom ? 'Có' : 'Không' }}
                </b-badge>
              </b-col>
              <b-col md="6" class="intake-field">
                <span>Hạn bảo hành:</span>
                {{ formatDate(selectedVehicle.warrantyEndDate) }}
              </b-col>
            </b-row>

            <h6 class="intake-card-title mt-3">Lịch sử sửa chữa</h6>
            <b-table
              v-if="vehicleHistory.length"
              :items="vehicleHistory"
              :fields="historyFields"
              small
              bordered
              responsive
            >
              <template #cell(totalAfterVAT)="row">
                {{ formatPrice(row.item.totalAfterVAT) }}
              </template>
              <template #cell(createdAt)="row">
                {{ formatDate(row.item.createdAt) }}
              </template>
              <template #cell(status)="row">
                <b-badge :variant="statusVariant(row.item.status)">
                  {{ statusLabel(row.item.status) }}
                </b-badge>
              </template>
            </b-table>
            <p v-else class="text-muted">
              Xe chưa có lịch sử sửa chữa tại showroom
            </p>
          </div>
        </div>

        <!-- Card 2: Bảo hiểm + tiếp nhận -->
        <div class="intake-card mt-4">
          <h6 class="intake-card-title">
            Công ty bảo hiểm (không bắt buộc — chọn nếu phiếu GBH)
          </h6>
          <b-row>
            <b-col md="6">
              <label class="intake-label">Công ty bảo hiểm thanh toán</label>
              <b-form-select
                v-model="form.insuranceCompanyId"
                size="sm"
                :options="insuranceOptions"
              />
            </b-col>
            <b-col md="6">
              <label class="intake-label">Giám định viên</label>
              <b-input-group size="sm">
                <multiselect
                  v-model="selectedSurveyor"
                  :options="surveyorOpts"
                  :show-labels="false"
                  track-by="id"
                  label="name"
                  placeholder="Chọn giám định viên"
                  open-direction="bottom"
                  class="intake-surveyor"
                  @input="onSurveyorChange"
                >
                  <span slot="noOptions">Chưa có giám định viên</span>
                  <span slot="noResult">Không tìm thấy — bấm + để thêm mới</span>
                </multiselect>
                <b-input-group-append>
                  <span
                    class="input-group-text sub-add"
                    title="Thêm giám định viên mới"
                    @click="openAddSurveyor"
                  >
                    <i class="fas fa-plus"></i>
                  </span>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col md="6">
              <label class="intake-label">Người mang xe</label>
              <b-form-input v-model="form.carBringer" size="sm" />
            </b-col>
            <b-col md="6">
              <label class="intake-label">SĐT người mang xe</label>
              <b-form-input v-model="form.carBringerPhone" size="sm" />
            </b-col>
            <b-col md="12">
              <label class="intake-label">Yêu cầu khách hàng</label>
              <b-form-textarea
                v-model="form.customerRequest"
                size="sm"
                rows="2"
              />
            </b-col>
          </b-row>
        </div>

        <div class="mt-4 text-right">
          <b-button
            variant="primary"
            :disabled="!selectedVehicle || submitting"
            @click="confirm"
          >
            <b-spinner v-if="submitting" small />
            Xác nhận tạo phiếu
          </b-button>
        </div>
      </template>
    </KTCodePreview>

    <AddCustomerVehicleModal
      ref="addCustomerModal"
      @created="onVehicleCreated"
      @updated="onVehicleUpdated"
    />

    <!-- Quick-add GĐV — cùng bố cục modal "Thêm mới nhà cung cấp" (stocks/AddStock.vue). -->
    <b-modal
      v-model="showSurveyorModal"
      hide-footer
      title="Thêm mới giám định viên"
    >
      <b-row class="mb-5">
        <b-col>
          <label class="labelInput">Tên giám định viên:</label>
          <b-form-input
            v-model="surveyorName"
            size="sm"
            placeholder="Nhập tên giám định viên"
            @keyup.enter="createSurveyor"
          />
        </b-col>
        <b-col>
          <label class="labelInput">Số điện thoại giám định viên:</label>
          <b-form-input
            v-model="surveyorPhone"
            size="sm"
            placeholder="Nhập số điện thoại giám định viên"
            @keyup.enter="createSurveyor"
          />
        </b-col>
      </b-row>
      <b-row class="ml-1">
        <b-overlay
          :show="isSavingSurveyor"
          opacity="0.6"
          spinner-variant="white"
          spinner-type="grow"
          spinner-small
        >
          <b-button
            style="font-weight: 600; width: 70px"
            variant="primary"
            size="sm"
            @click="createSurveyor"
            >Lưu
          </b-button>
        </b-overlay>
        <b-button
          style="margin-left: 10px; font-weight: 600; width: 70px"
          variant="secondary"
          size="sm"
          @click="showSurveyorModal = false"
          >Hủy
        </b-button>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';
import debounce from 'debounce';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ServiceOrderService from '@/core/services/serviceOrder.service';
import InsuranceService from '@/core/services/insurance.service';
import {
  SO_STATUS_LABEL,
  SO_STATUS_VARIANT,
} from '@/core/constants/serviceOrder';
import { get } from 'lodash';
import { makeToastSuccess, makeToastFaile, convertPrice } from '@/utils/common';
import AddCustomerVehicleModal from '@/view/components/service-orders/AddCustomerVehicleModal.vue';

export default {
  name: 'VehicleIntake',
  components: { KTCodePreview, AddCustomerVehicleModal, Multiselect },
  data() {
    return {
      keyword: '',
      searching: false,
      searchResults: [],
      showResults: false,
      selectedVehicle: null,
      vehicleHistory: [],
      insuranceCompanies: [],
      surveyorOpts: [],
      selectedSurveyor: null,
      // Quick-add GĐV (modal) — giống modal "Thêm mới nhà cung cấp".
      showSurveyorModal: false,
      surveyorName: '',
      surveyorPhone: '',
      isSavingSurveyor: false,
      submitting: false,
      form: {
        insuranceCompanyId: null,
        surveyorId: null,
        surveyorName: '',
        carBringer: '',
        carBringerPhone: '',
        customerRequest: '',
      },
      historyFields: [
        { key: 'orderNo', label: 'Số phiếu' },
        { key: 'createdAt', label: 'Ngày tạo' },
        { key: 'totalAfterVAT', label: 'Tổng tiền' },
        { key: 'status', label: 'Trạng thái' },
      ],
    };
  },
  computed: {
    insuranceOptions() {
      return [
        { value: null, text: '-- Không (BDSC) --' },
        ...this.insuranceCompanies.map((c) => ({
          value: c.id,
          text: c.shortName || c.name,
        })),
      ];
    },
  },
  created() {
    this.onSearchInput = debounce(this.runSearch, 350);
    this.loadInsuranceCompanies();
    this.loadSurveyors();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Phiếu dịch vụ', route: '/service-orders' },
      { title: 'Tiếp nhận xe' },
    ]);
  },
  methods: {
    // Nhãn + màu trạng thái phiếu — dùng chung map với màn Danh sách (không hiện enum thô).
    statusLabel(s) {
      return SO_STATUS_LABEL[s] || s;
    },
    statusVariant(s) {
      return SO_STATUS_VARIANT[s] || 'secondary';
    },
    formatDate(d) {
      return d ? moment(d).format('DD/MM/YYYY') : '—';
    },
    formatPrice(v) {
      return convertPrice(v || 0) + ' đ';
    },
    async runSearch() {
      const kw = (this.keyword || '').trim();
      if (kw.length < 2) {
        this.searchResults = [];
        this.showResults = false;
        return;
      }
      this.searching = true;
      try {
        const res = await ServiceOrderService.searchVehicle(kw, 20);
        this.searchResults = (res.data && res.data.data) || [];
        this.showResults = true;
      } catch (e) {
        makeToastFaile('Lỗi tìm kiếm xe');
      } finally {
        this.searching = false;
      }
    },
    async selectVehicle(v) {
      this.selectedVehicle = v;
      this.showResults = false;
      this.keyword = `${v.licensePlate} · ${v.customerName}`;
      await this.loadHistory(v.licensePlate);
    },
    async loadHistory(licensePlate) {
      try {
        const res = await ServiceOrderService.getAll({
          licensePlate,
          limit: 50,
        });
        const body = (res.data && res.data.data) || [];
        this.vehicleHistory = Array.isArray(body) ? body : body.data || [];
      } catch (e) {
        this.vehicleHistory = [];
      }
    },
    async loadInsuranceCompanies() {
      try {
        const res = await InsuranceService.getCompanies({ limit: 200 });
        const body = (res.data && res.data.data) || [];
        this.insuranceCompanies = Array.isArray(body) ? body : body.data || [];
      } catch (e) {
        this.insuranceCompanies = [];
      }
    },
    // Danh mục GĐV PHẲNG — tải một lần, KHÔNG phụ thuộc công ty BH (Q&A #12).
    async loadSurveyors() {
      try {
        const res = await InsuranceService.getSurveyors({});
        const list = (res.data && res.data.data) || [];
        this.surveyorOpts = (Array.isArray(list) ? list : []).map((s) => ({
          id: s.id,
          name: s.fullName,
        }));
      } catch (e) {
        this.surveyorOpts = [];
      }
    },
    openAddSurveyor() {
      this.surveyorName = '';
      this.surveyorPhone = '';
      this.showSurveyorModal = true;
    },
    // Thêm GĐV vào danh mục rồi CHỌN LUÔN cho phiếu. Trùng tên → BE trả lại bản ghi cũ.
    async createSurveyor() {
      const fullName = (this.surveyorName || '').trim();
      if (!fullName || this.isSavingSurveyor) return;
      this.isSavingSurveyor = true;
      try {
        const res = await InsuranceService.createSurveyor({
          fullName,
          phone: (this.surveyorPhone || '').trim() || undefined,
        });
        const saved = (res.data && res.data.data) || {};
        const opt = { id: saved.id, name: saved.fullName || fullName };
        if (!this.surveyorOpts.some((o) => o.id === opt.id)) {
          this.surveyorOpts.push(opt);
        }
        this.selectedSurveyor = opt;
        this.onSurveyorChange(opt);
        this.showSurveyorModal = false;
        makeToastSuccess('Thêm giám định viên thành công');
      } catch (e) {
        makeToastFaile(
          get(e, 'response.data.message') || 'Không thêm được giám định viên',
        );
      } finally {
        this.isSavingSurveyor = false;
      }
    },
    onSurveyorChange(opt) {
      this.form.surveyorId = opt && opt.id ? opt.id : null;
      this.form.surveyorName = opt ? opt.name : '';
    },
    openAddCustomer() {
      this.$refs.addCustomerModal.show();
    },
    // Mở modal SỬA hồ sơ xe cho xe đang chọn (tái dùng AddCustomerVehicleModal).
    editVehicle() {
      this.$refs.addCustomerModal.showEdit(this.selectedVehicle);
    },
    // Sau khi sửa: gộp thuộc tính xe mới vào hồ sơ đang hiển thị (giữ tên KH/SĐT sẵn có).
    onVehicleUpdated(updated) {
      this.selectedVehicle = {
        ...this.selectedVehicle,
        ...updated,
        productName: updated.productName || this.selectedVehicle.productName,
      };
      makeToastSuccess('Cập nhật hồ sơ xe thành công');
    },
    onVehicleCreated(vehicle) {
      // vehicle = bản ghi service_vehicles vừa tạo → đổ vào search & chọn.
      const mapped = {
        id: vehicle.id,
        licensePlate: vehicle.licensePlate,
        productName: vehicle.productName || '',
        customerName: vehicle.customerName || '',
        customerPhone: vehicle.customerPhone || '',
        chassisNumber: vehicle.chassisNumber,
        warrantyEndDate: vehicle.warrantyEndDate,
        isSoldByShowroom: vehicle.isSoldByShowroom,
      };
      this.selectVehicle(mapped);
    },
    async confirm() {
      if (!this.selectedVehicle) return;
      this.submitting = true;
      try {
        const payload = {
          serviceVehicleId: this.selectedVehicle.id,
          insuranceCompanyId: this.form.insuranceCompanyId || undefined,
          surveyorId: this.form.surveyorId || undefined,
          surveyorName: this.form.surveyorName || undefined,
          carBringer: this.form.carBringer || undefined,
          carBringerPhone: this.form.carBringerPhone || undefined,
          customerRequest: this.form.customerRequest || undefined,
        };
        const res = await ServiceOrderService.create(payload);
        const order = (res.data && res.data.data) || {};
        makeToastSuccess(`Tạo báo giá thành công: ${order.orderNo || ''}`);
        this.$router.push({ name: 'list-service-order' });
      } catch (e) {
        makeToastFaile('Tạo phiếu thất bại');
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Multiselect nằm trong b-input-group: chiếm hết chỗ trống, chừa nút "+" bên phải. */
.intake-surveyor {
  flex: 1 1 auto;
  width: 1%;
  min-height: 0;
}
.sub-add {
  cursor: pointer;
}
/* Thu nhỏ multiselect cho bằng các ô `size="sm"` cạnh nó (bootstrap sm ≈ 31px, font .875rem).
   Mặc định vue-multiselect cao 40px / font 16px nên trông to hơn hẳn. */
.intake-surveyor ::v-deep .multiselect__tags {
  min-height: 31px;
  padding: 4px 40px 0 8px;
  font-size: 0.875rem;
  border-color: #e4e6ef;
  /* Nối liền với nút "+" bên phải trong input-group. */
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.intake-surveyor ::v-deep .multiselect__single,
.intake-surveyor ::v-deep .multiselect__placeholder,
.intake-surveyor ::v-deep .multiselect__input {
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 4px;
  padding-top: 0;
  min-height: 20px;
}
.intake-surveyor ::v-deep .multiselect__select {
  height: 29px;
  padding: 0 8px;
}
.intake-surveyor ::v-deep .multiselect__option {
  min-height: 32px;
  padding: 8px 12px;
  font-size: 0.875rem;
}
.intake-card {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 16px;
}
.intake-card-title {
  font-weight: 600;
  color: #3f4254;
}
.intake-label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 2px;
}
.intake-results {
  position: absolute;
  z-index: 20;
  width: 100%;
  max-height: 280px;
  overflow-y: auto;
}
.intake-search-hint {
  font-size: 0.8rem;
  color: #7e8299;
}
.intake-field {
  padding: 4px 12px;
}
.intake-field span {
  color: #7e8299;
}
</style>
